import { NotificationType } from './enum/notificationType.enum.js';
/**
 * Which actionable notifications a completion event elsewhere in the app
 * answers (PRD #1453, RS-007).
 *
 * The complement of {@link NOTIFICATION_DISPOSITION}: that table says whether a
 * type still awaits its recipient, this one says what finishing it looks like
 * when the answer is given somewhere other than the Posteingang. An actionable
 * row leaves Offen when the thing it asked for actually happened — never on a
 * timer, and never because the user merely looked at it.
 *
 * Only types that a *domain event the app already publishes* can settle appear
 * here. The deliberately absent ones are recorded on the disposition table:
 * `CRITICAL_PROGRAM_VALUES`, `PROGRAM_EXPIRED`, `NEW_INVOICE_AVAILABLE` and the
 * three document announcements have no completion signal, so they sit in Offen
 * until archived by hand. That is a missing destination showing through, not an
 * oversight — see the disposition table for why a fallback timer would hide it.
 */
/**
 * `appointment.participant-status-changed` settles the invitation to that one
 * appointment, and so does `appointment.participant-withdrew`: a patient who
 * calls the appointment off has nothing left to answer (TM-9535).
 *
 * Not a list, because the invite is the only type the answer speaks to: the
 * confirmations the same event *creates* (APPOINTMENT_ACCEPTED / …_DECLINED,
 * and the patient-facing …_SUCCESS pair) are announcements of the answer, and
 * are informational.
 */
export const NOTIFICATION_TYPE_ANSWERED_BY_PARTICIPANT_STATUS = NotificationType.APPOINTMENT_INVITE;
/**
 * Exercise notifications a completed training session answers **only when the
 * session was on a program the notification names**.
 *
 * Scoped to the program on purpose. A patient can hold several programs at
 * once, and `NEW_EXERCISE_PROGRAM` / `EXERCISE_PROGRAM_UPDATED` carry the
 * therapist's "Information für den Patienten" — which is rendered nowhere else
 * in the product. Archiving every exercise row on any completed session would
 * file a message the patient has not read yet out of the queue on the strength
 * of them having trained for a different program.
 *
 * `TODAYS_EXERCISE_SCHEDULE` names every program due that day, so a session on
 * any one of them clears it. The nudge is redundant with the programs
 * themselves and is re-issued daily, so clearing it slightly early costs
 * nothing — which is not true of a message shown only here.
 */
export const EXERCISE_TYPES_ANSWERED_BY_A_SESSION_ON_THE_SAME_PROGRAM = [
    NotificationType.NEW_EXERCISE_PROGRAM,
    NotificationType.EXERCISE_PROGRAM_UPDATED,
    NotificationType.TODAYS_EXERCISE_SCHEDULE,
];
/**
 * Exercise notifications a completed training session answers **whichever
 * program it was for**.
 *
 * `EXERCISE_PROGRAM_COMPLETION_REMINDER` is dispatched from
 * `training-session.completion-reminder` and names no program at all — it says
 * "you started a session and did not finish it". Any completed session is an
 * answer to that, and there is nothing to scope it by even if it were not.
 */
export const EXERCISE_TYPES_ANSWERED_BY_ANY_SESSION = [
    NotificationType.EXERCISE_PROGRAM_COMPLETION_REMINDER,
];
//# sourceMappingURL=notification-completion.js.map